import { Controller } from "@hotwired/stimulus"
import { Notyf } from "notyf"

export default class extends Controller {
  static values = {
    notice: String,
    alert: String,
    success: String,
    error: String,
    info: String,
    warning: String
  }

  connect() {
    this.initializeNotyf()
    this.makeGloballyAccessible()
    this.bindEventListeners()
    this.showFlashMessages()
  }

  initializeNotyf() {
    this.notyf = new Notyf({
      duration: 4000,
      position: {
        x: 'right',
        y: 'bottom'
      },
      types: [
        {
          type: 'warning',
          background: '#f59e0b',
          icon: false
        },
        {
          type: 'info',
          background: '#3b82f6',
          icon: false
        }
      ]
    })
  }

  makeGloballyAccessible() {
    // Make this controller accessible globally
    this.element.flashController = this


    window.flashController = this
  }

  bindEventListeners() {
    this.element.addEventListener('flash:show', this.handleFlashEvent.bind(this))
    this.element.addEventListener('flash:error', this.handleErrorEvent.bind(this))
    this.element.addEventListener('flash:success', this.handleSuccessEvent.bind(this))
    this.element.addEventListener('flash:warning', this.handleWarningEvent.bind(this))
    this.element.addEventListener('flash:info', this.handleInfoEvent.bind(this))

    document.addEventListener('flash:show', this.handleFlashEvent.bind(this))
    document.addEventListener('flash:error', this.handleErrorEvent.bind(this))
    document.addEventListener('flash:success', this.handleSuccessEvent.bind(this))
    document.addEventListener('flash:warning', this.handleWarningEvent.bind(this))
    document.addEventListener('flash:info', this.handleInfoEvent.bind(this))
  }

  handleFlashEvent(event) {
    const { message, type = 'success' } = event.detail
    this.showToast(message, type)
  }

  handleErrorEvent(event) {
    const { message, context } = event.detail
    const errorMessage = context ? `${context}: ${message}` : message
    this.showToast(errorMessage, 'error')
  }

  handleSuccessEvent(event) {
    const { message } = event.detail
    this.showToast(message, 'success')
  }

  handleWarningEvent(event) {
    const { message } = event.detail
    this.showToast(message, 'warning')
  }

  handleInfoEvent(event) {
    const { message } = event.detail
    this.showToast(message, 'info')
  }

  showFlashMessages() {
    const flashTypes = [
      { value: this.noticeValue, type: 'success', hasValue: this.hasNoticeValue },
      { value: this.alertValue, type: 'error', hasValue: this.hasAlertValue },
      { value: this.successValue, type: 'success', hasValue: this.hasSuccessValue },
      { value: this.errorValue, type: 'error', hasValue: this.hasErrorValue },
      { value: this.infoValue, type: 'info', hasValue: this.hasInfoValue },
      { value: this.warningValue, type: 'warning', hasValue: this.hasWarningValue }
    ]

    flashTypes.forEach(({ value, type, hasValue }) => {
      if (hasValue && value) {
        this.showToast(value, type)
      }
    })
  }

  // Method to show toast from anywhere in your app
  showToast(message, type = 'success') {
    if (!message || !this.notyf) return

    switch(type) {
      case 'success':
        this.notyf.success(message)
        break
      case 'error':
        this.notyf.error(message)
        break
      case 'warning':
        this.notyf.open({
          type: 'warning',
          message: message
        })
        break
      case 'info':
        this.notyf.open({
          type: 'info',
          message: message
        })
        break
      default:
        this.notyf.success(message)
    }
  }

  static dispatchFlash(message, type = 'success', context = null) {
    const eventDetail = { message, type }
    if (context) eventDetail.context = context

    const event = new CustomEvent('flash:show', { detail: eventDetail })
    document.dispatchEvent(event)
  }

  static dispatchError(message, context = null) {
    const event = new CustomEvent('flash:error', {
      detail: { message, context }
    })
    document.dispatchEvent(event)
  }

  static dispatchSuccess(message) {
    const event = new CustomEvent('flash:success', {
      detail: { message }
    })
    document.dispatchEvent(event)
  }

  static dispatchWarning(message) {
    const event = new CustomEvent('flash:warning', {
      detail: { message }
    })
    document.dispatchEvent(event)
  }

  static dispatchInfo(message) {
    const event = new CustomEvent('flash:info', {
      detail: { message }
    })
    document.dispatchEvent(event)
  }

  disconnect() {
    // Clean up event listeners
    this.element.removeEventListener('flash:show', this.handleFlashEvent.bind(this))
    this.element.removeEventListener('flash:error', this.handleErrorEvent.bind(this))
    this.element.removeEventListener('flash:success', this.handleSuccessEvent.bind(this))
    this.element.removeEventListener('flash:warning', this.handleWarningEvent.bind(this))
    this.element.removeEventListener('flash:info', this.handleInfoEvent.bind(this))

    document.removeEventListener('flash:show', this.handleFlashEvent.bind(this))
    document.removeEventListener('flash:error', this.handleErrorEvent.bind(this))
    document.removeEventListener('flash:success', this.handleSuccessEvent.bind(this))
    document.removeEventListener('flash:warning', this.handleWarningEvent.bind(this))
    document.removeEventListener('flash:info', this.handleInfoEvent.bind(this))

    // Clean up global references
    if (this.element.flashController === this) {
      delete this.element.flashController
    }
    if (window.flashController === this) {
      delete window.flashController
    }
  }
}
